import { Vector2 } from 'three';

/**
 * Pure coordinate transforms between the X-ray image's pixel space and the
 * normalized landmark frame the API uses. Extracted from the edit ViewModel so
 * the reactive store body stays focused on edit state, not geometry math.
 */

/**
 * Clamp a position (in image pixel space) to the image bounds. A null image
 * size (image not yet loaded) leaves the position untouched.
 */
export function clampToImage(
    position: Vector2,
    imageSize: { width: number; height: number } | null,
): Vector2 {
    if (!imageSize) return position;
    const x = Math.min(Math.max(position.x, 0), imageSize.width);
    const y = Math.min(Math.max(position.y, 0), imageSize.height);
    return new Vector2(x, y);
}

/**
 * Convert an image-pixel position to the API's normalized landmark frame: x as
 * a fraction of width, y as a fraction of height measured from the BOTTOM edge
 * (the normalized origin is bottom-left; the pixel origin is top-left).
 */
export function imagePxToNormalized(
    position: Vector2,
    imageSize: { width: number; height: number },
): { current_x: number; current_y: number } {
    return {
        current_x: position.x / imageSize.width,
        current_y: 1 - position.y / imageSize.height,
    };
}

/**
 * Convert a landmark's normalized API coords to a point in image-pixel scene
 * space: x as a fraction of width, y flipped (the normalized origin is
 * bottom-left; the scene/pixel frame is y-up from the top). Inverse of
 * `imagePxToNormalized`.
 *
 * NOTE: this is the y-FLIPPED scene/draw projection. The angle pipeline's
 * y-DOWN frame (`current_y * height`, no flip) does NOT use this helper.
 */
export function landmarkToImagePoint(
    landmark: { current_x: number; current_y: number },
    imageSize: { width: number; height: number },
): Vector2 {
    return new Vector2(
        landmark.current_x * imageSize.width,
        (1 - landmark.current_y) * imageSize.height,
    );
}
